// CRUD de productos + entrada/salida de stock. Port de views/productos.js.
import { useState } from 'react';
import { useProductos, useCrearProducto, useEditarProducto, useEliminarProducto } from '../hooks/useProductos';
import { useBodegas } from '../hooks/useBodegas';
import { idBodegaPrincipal } from '../lib/api/bodegas';
import { useAgregarStock, useDescontarStock } from '../hooks/useInventario';
import { DataTable, type Column } from '../components/DataTable';
import { Badge } from '../components/Badge';
import { Thumb } from '../components/Thumb';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';
import { FotoUploader } from '../components/FotoUploader';
import { SkeletonTable } from '../components/Skeletons';
import { ErrorBox } from '../components/ErrorBox';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/Confirm';
import type { Bodega, Estado, Producto } from '../types';

function StockBadge({ p }: { p: Producto }) {
  const total = p.stockTotal ?? 0;
  if (total <= 0) return <Badge type="danger">Agotado</Badge>;
  if (p.stock_minimo && total <= p.stock_minimo) return <Badge type="warning">Bajo</Badge>;
  return <Badge type="success">Disponible</Badge>;
}

export function Productos() {
  const { data: productos, isLoading, isError, error } = useProductos();
  const { data: bodegas } = useBodegas();
  const [form, setForm] = useState<{ open: boolean; prod?: Producto }>({ open: false });
  const [stock, setStock] = useState<Producto | null>(null);
  const eliminar = useEliminarProducto();
  const toast = useToast();
  const confirm = useConfirm();

  const abrirNuevo = () => setForm({ open: true, prod: undefined });

  const borrar = async (p: Producto) => {
    const ok = await confirm({ title: 'Eliminar producto', message: `¿Eliminar "${p.nombre}" y su stock en todas las bodegas?` });
    if (!ok) return;
    try {
      await eliminar.mutateAsync(p.id);
      toast.success('Producto eliminado');
    } catch (e) {
      toast.error('No se pudo eliminar', (e as Error).message);
    }
  };

  const cols: Column<Producto>[] = [
    {
      key: 'nombre', label: 'Producto',
      render: (r) => (
        <div className="cell-item">
          <Thumb foto={r.foto} icon="fa-box" />
          <div><b>{r.nombre}</b><br /><small>{r.sku || r.unidad || ''}</small></div>
        </div>
      ),
    },
    { key: 'stockTotal', label: 'Stock total', render: (r) => (<><b>{r.stockTotal}</b> <small className="text-muted">{r.unidad || ''}</small></>) },
    { key: 'stock_minimo', label: 'Mínimo', render: (r) => r.stock_minimo },
    { key: 'estado', label: 'Estado', render: (r) => <StockBadge p={r} /> },
    {
      key: '_acc', label: '', sortable: false,
      render: (r) => (
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
          <button className="btn ghost sm icon-only" title="Mover stock" onClick={() => setStock(r)}><i className="fa-solid fa-arrow-right-arrow-left" /></button>
          <button className="btn ghost sm icon-only" title="Editar" onClick={() => setForm({ open: true, prod: r })}><i className="fa-solid fa-pen" /></button>
          <button className="btn ghost sm icon-only" title="Eliminar" onClick={() => borrar(r)}><i className="fa-solid fa-trash" /></button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-head">
        <h2>Productos</h2>
        <button className="btn primary" onClick={abrirNuevo}><i className="fa-solid fa-plus" /> Nuevo producto</button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} />
      ) : isError ? (
        <ErrorBox mensaje={(error as Error).message} />
      ) : (
        <DataTable title="Catálogo de productos" pageSize={10} cols={cols} rows={productos ?? []} empty="No hay productos. Crea el primero." />
      )}

      {form.open ? <ProductoModal prod={form.prod} onClose={() => setForm({ open: false })} /> : null}
      {stock ? <StockModal prod={stock} bodegas={bodegas ?? []} onClose={() => setStock(null)} /> : null}
    </>
  );
}

function ProductoModal({ prod, onClose }: { prod?: Producto; onClose: () => void }) {
  const editar = !!prod;
  const crear = useCrearProducto();
  const editarM = useEditarProducto();
  const toast = useToast();
  const [f, setF] = useState({
    nombre: prod?.nombre ?? '',
    sku: prod?.sku ?? '',
    unidad: prod?.unidad ?? 'Unidad',
    stock_minimo: String(prod?.stock_minimo ?? 0),
    foto: prod?.foto ?? '',
    descripcion: prod?.descripcion ?? '',
    estado: (prod?.estado ?? 'Activo') as Estado,
  });
  const [enviado, setEnviado] = useState(false);
  const cargando = crear.isPending || editarM.isPending;
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const guardar = async () => {
    setEnviado(true);
    if (!f.nombre.trim()) return;
    const input = { ...f, stock_minimo: Number(f.stock_minimo) || 0 };
    try {
      if (editar) await editarM.mutateAsync({ id: prod!.id, input });
      else await crear.mutateAsync(input);
      toast.success(editar ? 'Producto actualizado' : 'Producto creado', f.nombre);
      onClose();
    } catch (e) {
      toast.error('No se pudo guardar', (e as Error).message);
    }
  };

  return (
    <Modal
      open size="lg"
      title={editar ? 'Editar producto' : 'Nuevo producto'}
      icon="fa-box" iconColor="info"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className={`btn primary${cargando ? ' loading' : ''}`} onClick={guardar}>{editar ? 'Guardar cambios' : 'Crear producto'}</button>
        </>
      }
    >
      <div className="field-row">
        <Field name="nombre" label="Nombre" required value={f.nombre} onChange={set('nombre')} invalid={enviado && !f.nombre.trim()} />
        <Field name="sku" label="SKU / Código" value={f.sku} onChange={set('sku')} />
      </div>
      <div className="field-row">
        <Field name="unidad" label="Unidad" value={f.unidad} onChange={set('unidad')} placeholder="Unidad, Caja, Frasco..." />
        <Field name="stock_minimo" label="Stock mínimo" type="number" min={0} value={f.stock_minimo} onChange={set('stock_minimo')} hint="Genera alertas cuando el stock baja de este valor." />
      </div>
      <FotoUploader value={f.foto} onChange={(url) => setF((s) => ({ ...s, foto: url }))} />
      <Field name="descripcion" label="Descripción" type="textarea" value={f.descripcion} onChange={set('descripcion')} />
      <Field name="estado" label="Estado" type="select" value={f.estado} onChange={set('estado')}
        options={[{ value: 'Activo', label: 'Activo' }, { value: 'Inactivo', label: 'Inactivo' }]} />
    </Modal>
  );
}

function StockModal({ prod, bodegas, onClose }: { prod: Producto; bodegas: Bodega[]; onClose: () => void }) {
  const agregar = useAgregarStock();
  const descontar = useDescontarStock();
  const toast = useToast();
  const [operacion, setOperacion] = useState<'agregar' | 'descontar'>('agregar');
  const [bodegaId, setBodegaId] = useState(idBodegaPrincipal(bodegas));
  const [cantidad, setCantidad] = useState('');
  const [enviado, setEnviado] = useState(false);
  const cargando = agregar.isPending || descontar.isPending;

  if (!bodegas.length) {
    return (
      <Modal open title="Mover stock" icon="fa-arrow-right-arrow-left" onClose={onClose}
        footer={<button className="btn ghost" onClick={onClose}>Cerrar</button>}>
        <p className="text-muted" style={{ margin: 0 }}>Crea una bodega primero.</p>
      </Modal>
    );
  }

  const aplicar = async () => {
    setEnviado(true);
    const cant = Number(cantidad);
    if (!bodegaId || !cant || cant <= 0) return;
    const op = { bodegaId, tipo: 'producto' as const, refId: prod.id, cantidad: cant };
    try {
      if (operacion === 'agregar') await agregar.mutateAsync(op);
      else await descontar.mutateAsync(op);
      toast.success('Stock actualizado', prod.nombre);
      onClose();
    } catch (e) {
      toast.error('No se pudo actualizar', (e as Error).message);
    }
  };

  return (
    <Modal
      open
      title={`Mover stock — ${prod.nombre}`}
      icon="fa-arrow-right-arrow-left" iconColor="primary"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className={`btn primary${cargando ? ' loading' : ''}`} onClick={aplicar}>Aplicar</button>
        </>
      }
    >
      <Field name="operacion" label="Operación" type="select" value={operacion} onChange={(v) => setOperacion(v as 'agregar' | 'descontar')}
        options={[{ value: 'agregar', label: 'Agregar (entrada)' }, { value: 'descontar', label: 'Descontar (salida)' }]} />
      <Field name="bodegaId" label="Bodega" type="select" required value={bodegaId} onChange={setBodegaId}
        options={bodegas.map((b) => ({ value: b.id, label: b.principal ? `${b.nombre} (principal)` : b.nombre }))} invalid={enviado && !bodegaId} />
      <Field name="cantidad" label="Cantidad" type="number" required min={1} value={cantidad} onChange={setCantidad}
        invalid={enviado && (!cantidad || Number(cantidad) <= 0)} />
    </Modal>
  );
}
